import { Avatar, Box, IconButton, Typography } from "@mui/material";
import {
  communityDetailsContainerStyle,
  communityDetailsSubContainerStyle,
  communityDetailsHeadingStyle,
  communityDetailsTextStyle,
  clientDetailsWrapper,
  communityDetailsLogoTextWrapper,
  clientCommunityDetailsContainerStyle,
  timeLocationRowStyle,
  timeLocationKeyStyle,
  timeLocationValueStyle,
  totalRowStyle,
  totalRowTextStyle,
  VATTextStyle,
} from "../styles/styles";
import { formatDateToDDMYY } from "../../../Utils/dateFormat";
import { formatPrice } from "../../../Helpers/formating";
import CustomTooltip from "../../../Components/CustomTooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSnackbar } from "../../../Components/SnackBar";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { getCurrencySymbol } from "../../../Utils/CountriesData/getCountriesData";

/**
 * Product Job page — right panel: Client & Community, fulfillment Time &
 * Location (with a Delivery/Pickup tag), and the supplier Order Summary
 * (Price, Delivery Price, Community discount as an AMOUNT — different product
 * categories carry different rates — Keilot Fee, Total). COMY1-283.
 */
const ProductClientCommunityDetails = (props) => {
  const { jobData, viewType } = props;
  const {
    client_community_details,
    fulfillment,
    contact_info,
    order_summary,
    ordered_products = [],
  } = jobData;
  const { member, community } = client_community_details;
  const showSnackbar = useSnackbar();

  const { t: ttable } = useTranslation("page-my-jobs", { keyPrefix: "table_data" });
  const { t } = useTranslation("view-job-modal");
  const { t: tHatavot } = useTranslation("hatavot");
  const theme = useTheme();

  const currencySymbol = getCurrencySymbol(order_summary?.currency);
  const isPending = viewType === "pending";
  const isMissed = viewType === "missed";
  const isPickup = fulfillment?.type === "pickup";
  // A digital-only order is created with the pickup fulfillment type (the DB
  // has no digital one), but nothing is collected — it's delivered online, so
  // the tag and location say that instead of sending the client to the
  // business address (COMY1-334).
  const isDigitalOnly =
    ordered_products.length > 0 &&
    ordered_products.every((item) => item.is_digital);

  const timeLocationArr = [
    {
      [t("Created On")]: fulfillment?.created_at
        ? formatDateToDDMYY(fulfillment.created_at)
        : "-",
    },
    {
      [t("Due Date")]:
        fulfillment?.due_value != null
          ? `${fulfillment.due_value} ${t(`time_units.${fulfillment.due_unit}`, fulfillment.due_unit || "")}`
          : "-",
    },
    {
      // Pickup → the member collects from the supplier's store, so always show
      // a generic "At the business address" label (never a street address).
      // Delivery → the member's address is hidden before approval
      // (pending/missed) behind a generic "Client Address" placeholder, and
      // only revealed once active/completed. Matches the service flow.
      [t("Location")]: isDigitalOnly
        ? ttable("online")
        : isPickup
        ? ttable("business_address")
        : isPending || isMissed
          ? ttable("client_address")
          : fulfillment?.location || "-",
    },
  ];

  const orderSummaryArr = [
    {
      // Product prices are exact (not an estimate like services) — no "approx".
      [t("Price")]:
        order_summary?.price != null
          ? `${currencySymbol}${formatPrice(order_summary.price)}`
          : "-",
    },
    // הטבה discount line — only on orders that carried a הטבה. A (?) explains
    // exactly what the הטבה is, so the supplier understands its effect.
    ...(order_summary?.hatava_discount
      ? [{
          [tHatavot("job.discount_line")]: (
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
              {`‎-${currencySymbol}${formatPrice(order_summary.hatava_discount)}`}
              {order_summary?.hatava_note ? (
                <CustomTooltip title={order_summary.hatava_note} tooltipSxStyles={{ paddingY: "6px" }}>
                  <Box component="span" sx={{ display: "inline-flex", cursor: "pointer" }}>
                    <InfoOutlinedIcon sx={{ fontSize: 15, color: theme.palette.customColors.textFaint }} />
                  </Box>
                </CustomTooltip>
              ) : null}
            </Box>
          ),
        }]
      : []),
    {
      [ttable("delivery_price")]: order_summary?.delivery_price
        ? `${currencySymbol}${formatPrice(order_summary.delivery_price)}`
        : "-",
    },
    {
      // Amount, not %, because each product category can carry a different rate.
      [t("Community discount")]: order_summary?.community_discount
        ? `‎-${currencySymbol}${formatPrice(order_summary.community_discount)}`
        : "-",
    },
    {
      // CommunitMe fee shown as a percentage.
      [t("Keilot Fee")]: order_summary?.commy_fee_percent
        ? `${order_summary.commy_fee_percent}%`
        : "-",
    },
  ];

  const fulfillmentTag = (
    <Box
      sx={{
        fontSize: "12px",
        fontWeight: 500,
        color: "rgba(255, 151, 51, 1)",
        backgroundColor: "rgba(255, 151, 51, 0.16)",
        borderRadius: "8px",
        padding: "2px 10px",
      }}
    >
      {isDigitalOnly
        ? t("digital_product")
        : isPickup
          ? ttable("pickup")
          : ttable("delivery")}
    </Box>
  );

  return (
    <Box className="hide-scrollbar" sx={communityDetailsContainerStyle}>
      <Box
        sx={{
          ...communityDetailsSubContainerStyle,
          paddingTop: { xs: "0px", md: "24px" },
        }}
      >
        <Typography sx={communityDetailsHeadingStyle}>
          {t("client_community_details")}
        </Typography>

        <Box sx={clientCommunityDetailsContainerStyle}>
          <Box sx={clientDetailsWrapper}>
            <Box sx={communityDetailsLogoTextWrapper}>
              <Avatar
                alt={!isPending && !isMissed ? member?.name : ""}
                src={!isPending && !isMissed ? member?.image_url : ""}
                sx={{ width: 38, height: 38 }}
              />
              <Typography sx={communityDetailsTextStyle}>
                {isPending || isMissed ? t("member") : member?.name}
              </Typography>
            </Box>

            {!isPending &&
            !isMissed &&
            (contact_info?.email || contact_info?.phone) ? (
              <Box sx={{ ml: "-8px" }}>
                {contact_info?.email ? (
                  <CustomTooltip title={t("copy_email")} tooltipSxStyles={{ paddingY: "5px" }}>
                    <IconButton
                      onClick={() => {
                        navigator.clipboard.writeText(contact_info.email);
                        showSnackbar(t("email_copied_alert"), "success");
                      }}
                    >
                      <img src="/icons/mail-forward.svg" alt="mail" />
                    </IconButton>
                  </CustomTooltip>
                ) : null}
                {contact_info?.phone ? (
                  <CustomTooltip title={t("copy_phone")} tooltipSxStyles={{ paddingY: "5px" }}>
                    <IconButton
                      onClick={() => {
                        navigator.clipboard.writeText(contact_info.phone);
                        showSnackbar(t("phone_copied_alert"), "success");
                      }}
                    >
                      <img src="/icons/phone-outgoing.svg" alt="phone" />
                    </IconButton>
                  </CustomTooltip>
                ) : null}
                {contact_info?.phone ? (
                  <CustomTooltip title={t("open_whatsapp")} tooltipSxStyles={{ paddingY: "5px" }}>
                    <IconButton
                      onClick={(e) => {
                        e.preventDefault();
                        window.open(
                          `https://api.whatsapp.com/send/?phone=${contact_info.phone}&text=hello`,
                          "_blank",
                          "noopener,noreferrer",
                        );
                      }}
                    >
                      <img src="/icons/whatsapp-icon.svg" alt="whatsapp" />
                    </IconButton>
                  </CustomTooltip>
                ) : null}
              </Box>
            ) : null}
          </Box>

          <Box sx={communityDetailsLogoTextWrapper}>
            <Avatar alt={community?.name} src={community?.logo_url} sx={{ width: 38, height: 38 }} />
            <Typography sx={communityDetailsTextStyle}>{community?.name}</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ ...communityDetailsSubContainerStyle, border: isMissed && "none" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={communityDetailsHeadingStyle}>{t("time_location")}</Typography>
          {fulfillmentTag}
        </Box>
        <Box sx={{ ...clientCommunityDetailsContainerStyle, gap: "12px" }}>
          {timeLocationArr.map((item, idx) => (
            <Box key={`row-${idx}`} sx={timeLocationRowStyle}>
              <Typography sx={timeLocationKeyStyle}>{Object.keys(item)[0]}</Typography>
              <Typography sx={timeLocationValueStyle}>{Object.values(item)[0]}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          ...communityDetailsSubContainerStyle,
          border: "none",
          paddingBottom: { xs: "0px", md: "24px" },
          display: isMissed ? "none" : "flex",
        }}
      >
        <Typography sx={communityDetailsHeadingStyle}>{t("order_summary")}</Typography>

        <Box sx={{ ...clientCommunityDetailsContainerStyle, gap: "13px" }}>
          {orderSummaryArr.map((item, idx) => (
            <Box key={`key-${idx}`} sx={timeLocationRowStyle}>
              <Typography sx={timeLocationKeyStyle}>{Object.keys(item)[0]}</Typography>
              <Typography sx={communityDetailsTextStyle}>{Object.values(item)[0]}</Typography>
            </Box>
          ))}
        </Box>

        <Box sx={totalRowStyle}>
          <Typography sx={totalRowTextStyle}>
            {viewType === "completed" ? t("total_revenue") : t("total")}
            <Typography sx={VATTextStyle}>{t("VAT_included")}</Typography>
            {order_summary?.is_estimate && (
              <Typography sx={VATTextStyle}>{t("estimate_note")}</Typography>
            )}
          </Typography>
          <Typography sx={totalRowTextStyle}>
            {order_summary?.total != null
              ? currencySymbol + formatPrice(order_summary.total)
              : "-"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductClientCommunityDetails;
